import {
  DataType,
  type SettingTypeMetadata,
  type SettingTypeValueMap,
} from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createSettingsParam,
  createSettingsParams,
  ISettingsParam,
  PartialBy,
  SettingsParamType,
} from '@wix/tpa-settings';
import {
  AlignmentOptions,
  LayoutOptions,
  ServiceColor,
  SlotsAvailability,
  SubtitleSourceOptions,
  TimeSlotComponent,
} from '../../types/types';
import { AccessibilityHtmlTags } from '../../utils/accessibility/constants';
import { InitializeCalendarDateOptions } from '../../utils/bi/consts';
import { timeSlotLayouts } from '../../utils/layouts';

export const withoutDefaultFallback = (settingsParam: ISettingsParam) => ({
  ...settingsParam,
  getDefaultValue: () => '',
});

export type ISettingsParams = {
  columnAlignment: SettingsParamType.String;
  textAlignment: AlignmentOptions;
  slotAlignment: AlignmentOptions;
  calendarLayout: LayoutOptions;
  selectedLocations: SettingsParamType.Text[];
  selectedCategories: SettingsParamType.Text[];
  selectedService: SettingsParamType.String;
  dateAndTimeSectionHeader: SettingsParamType.String;
  sectionHeaderHtmlTag: SettingsParamType.String;
  bookingDetailsSectionHeader: SettingsParamType.String;
  headerVisibility: SettingsParamType.Boolean;
  headerTitleVisibility: SettingsParamType.Boolean;
  headerSubtitleVisibility: SettingsParamType.Boolean;
  headerFiltersVisibility: SettingsParamType.Boolean;
  headerServiceFilterVisibility: SettingsParamType.Boolean;
  headerStaffFilterVisibility: SettingsParamType.Boolean;
  headerLocationFilterVisibility: SettingsParamType.Boolean;
  headerTimezoneSelectionVisibility: SettingsParamType.Boolean;
  dateAndTimeTimezoneSelectionVisibility: SettingsParamType.Boolean;
  headerSubtitleSource: SubtitleSourceOptions;
  headerTitle: SettingsParamType.String;
  headerTitleHtmlTag: SettingsParamType.String;
  headerSubtitle: SettingsParamType.String;
  noSessionsOffered: SettingsParamType.String;
  noUpcomingTimeSlots: SettingsParamType.String;
  goToNextAvailableDate: SettingsParamType.String;
  fullyBookedDateNotification: SettingsParamType.String;
  loadMoreTimeSlots: SettingsParamType.String;
  limitTimeSlotsDisplay: SettingsParamType.Boolean;
  maxTimeSlotsDisplayedPerDay: SettingsParamType.Number;
  videoConferenceBadgeVisibility: SettingsParamType.Boolean;
  timeslotDetailsVisibility: SettingsParamType.Boolean;
  videoConferenceBadge: SettingsParamType.String;
  preferencesTitle: SettingsParamType.String;
  serviceLabel: SettingsParamType.String;
  staffMemberLabel: SettingsParamType.String;
  locationLabel: SettingsParamType.String;
  durationLabel: SettingsParamType.String;
  bookingDetailsPricingPlanText: SettingsParamType.String;
  bookingDetailsClearText: SettingsParamType.String;
  nextButton: SettingsParamType.String;
  noSpotsLeft: SettingsParamType.String;
  toEarlyToBookIndication: SettingsParamType.String;
  toLateToBookIndication: SettingsParamType.String;
  pendingApprovalButton: SettingsParamType.String;
  joinWaitlistButton: SettingsParamType.String;
  rescheduleButton: SettingsParamType.String;
  slotsAvailability: SlotsAvailability;
  initializeCalendarDate: InitializeCalendarDateOptions;
  waitlistIndication: SettingsParamType.String;
  buttonsFullWidth: SettingsParamType.Boolean;
  slotLocationVisibility: SettingsParamType.Boolean;
  slotDurationVisibility: SettingsParamType.Boolean;
  slotStaffMemberVisibility: SettingsParamType.Boolean;
  slotPriceVisibility: SettingsParamType.Boolean;
  spotsLeftVisibility: SettingsParamType.Boolean;
  slotRegistrationStatusVisibility: SettingsParamType.Boolean;
  spotsLeftFormat: SettingsParamType.Text;
  ctaVisibility: SettingsParamType.Boolean;
  useColorPerService: SettingsParamType.Boolean;
  servicesColorsType: ServiceColor;
  sideBordersVisibility: SettingsParamType.Boolean;
  timeSlotComponent: TimeSlotComponent;
  calendarHeaderTitle: SettingsParamType.String;
};

const getMetadata = <T extends DataType>(
  type: T,
  defaultValue: SettingTypeValueMap[T]['value'],
): SettingTypeMetadata => ({
  type: type as DataType.UNKNOWN_DataType, // type assertion is needed to avoid strange TypeScript error
  defaultValue: {
    type: 'static',
    value: defaultValue,
  },
});

const getMetadataWithMobileDefault = <T extends DataType>(
  type: T,
  defaultValue: SettingTypeValueMap[T]['value'],
  mobileDefaultValue: SettingTypeValueMap[T]['value'],
): SettingTypeMetadata => ({
  type: type as DataType.UNKNOWN_DataType, // type assertion is needed to avoid strange TypeScript error
  defaultValue: {
    type: 'static',
    value: defaultValue,
  },
  mobileDefaultValue: {
    type: 'static',
    value: mobileDefaultValue,
  },
});

export const settingsParam = <T extends DataType>(
  type: T,
  defaultValue: SettingTypeValueMap[T]['value'],
) => ({
  getDefaultValue: () => defaultValue,
  metadata: getMetadata(type, defaultValue),
});

export const settingsParamWithMobileDefault = <T extends DataType>(
  type: T,
  defaultValue: SettingTypeValueMap[T]['value'],
  mobileDefaultValue: SettingTypeValueMap[T]['value'],
  inheritDesktop?: boolean,
): Omit<ISettingsParam, 'key'> => ({
  getDefaultValue: ({ isMobile }) =>
    isMobile ? mobileDefaultValue : defaultValue,
  metadata: getMetadataWithMobileDefault(
    type,
    defaultValue,
    mobileDefaultValue,
  ),
  inheritDesktop,
});

export const enumSettingsParam = <T extends string>(defaultValue: T) => ({
  getDefaultValue: () => defaultValue,
  metadata: getMetadata(DataType.textEnum, defaultValue),
});

export const typedSettingsParam = <
  T extends DataType,
  Type extends SettingsParamType,
>(
  dataType: T,
  type: Type | undefined,
  defaultValue: SettingTypeValueMap[T]['value'],
) => ({
  type,
  ...settingsParam(dataType, defaultValue),
});

const typedRefSettingsParam = <
  T extends DataType,
  Type extends SettingsParamType,
>(
  dataType: T,
  type: Type,
  otherColorParam: any,
  otherColorParamName: string,
  fallback: SettingTypeValueMap[T]['value'],
): Omit<ISettingsParam, 'key'> => ({
  type,
  getDefaultValue: ({ getSettingParamValue }) =>
    getSettingParamValue(otherColorParam) ?? fallback,
  metadata: {
    type: dataType as DataType.UNKNOWN_DataType, // type assertion is needed to avoid strange TypeScript error,
    defaultValue: {
      type: 'ref',
      ref: otherColorParamName,
      fallback: {
        type: 'static',
        value: fallback,
      },
    },
  },
});

export const textSettingsParam = (
  defaultValueKey: string,
): Omit<ISettingsParam<SettingsParamType.Text>, 'key'> => ({
  type: SettingsParamType.Text,
  getDefaultValue: ({ t }) => t(defaultValueKey),
  metadata: ({ t }) => ({
    type: DataType.text,
    defaultValue: {
      type: 'static',
      value: t(defaultValueKey),
    },
  }),
});

export const booleanSettingsParam = (defaultValue: boolean) =>
  typedSettingsParam(
    DataType.booleanValue,
    SettingsParamType.Boolean,
    defaultValue,
  );

const enumSettingsParamWithRTLDefault = <T extends string>(
  rtlDefaultValue: T,
  ltrDefaultValue: T,
): Omit<ISettingsParam, 'key'> => ({
  getDefaultValue: ({ isRTL }) => (isRTL ? rtlDefaultValue : ltrDefaultValue),
  metadata: getMetadata(DataType.textEnum, ltrDefaultValue), // TODO: Fix metadata
});

const booleanSettingsParamWithoutInheritedDesktop = (
  defaultValue: boolean,
) => ({
  inheritDesktop: false,
  ...booleanSettingsParam(defaultValue),
});

const calendarLayout: ISettingsParam<LayoutOptions> = createSettingsParam(
  'calendarLayout',
  settingsParamWithMobileDefault(
    DataType.textEnum,
    LayoutOptions.DAILY_TIME_SLOTS_MONTHLY_PICKER,
    LayoutOptions.DAILY_TIME_SLOTS_WEEKLY_PICKER,
    false,
  ),
);

const headerFiltersVisibility = createSettingsParam(
  'headerFiltersVisibility',
  booleanSettingsParam(true),
);

export type ISettingsParamsDefinitions = {
  [key in keyof ISettingsParams]: PartialBy<
    ISettingsParam<ISettingsParams[key]>,
    'key'
  >;
};

export const settingsParamsDefinitions: ISettingsParamsDefinitions = {
  columnAlignment: settingsParam(DataType.textEnum, AlignmentOptions.CENTER),
  textAlignment: enumSettingsParamWithRTLDefault(
    AlignmentOptions.RIGHT,
    AlignmentOptions.LEFT,
  ),
  slotAlignment: {
    inheritDesktop: false,
    ...enumSettingsParamWithRTLDefault(
      AlignmentOptions.RIGHT,
      AlignmentOptions.LEFT,
    ),
  },
  calendarLayout,
  selectedLocations: typedSettingsParam(
    DataType.container,
    SettingsParamType.Object,
    [],
  ),
  selectedCategories: typedSettingsParam(
    DataType.container,
    SettingsParamType.Object,
    [],
  ),
  selectedService: typedSettingsParam(
    DataType.text,
    SettingsParamType.String,
    '',
  ),
  dateAndTimeSectionHeader: textSettingsParam(
    'app.settings.defaults.widget.date-and-time-header',
  ),
  sectionHeaderHtmlTag: typedSettingsParam(
    DataType.text,
    SettingsParamType.String,
    AccessibilityHtmlTags.SecondaryHeading,
  ),
  bookingDetailsSectionHeader: textSettingsParam(
    'app.settings.defaults.widget.booking-details-header',
  ),
  headerTitleVisibility: booleanSettingsParam(true),
  headerVisibility: booleanSettingsParam(true),
  headerTitleHtmlTag: typedSettingsParam(
    DataType.text,
    SettingsParamType.String,
    AccessibilityHtmlTags.MainHeading,
  ),
  headerSubtitleVisibility: booleanSettingsParam(true),
  headerFiltersVisibility,
  headerServiceFilterVisibility: typedRefSettingsParam(
    DataType.booleanValue,
    SettingsParamType.Boolean,
    headerFiltersVisibility,
    'headerFiltersVisibility',
    true,
  ),
  headerStaffFilterVisibility: typedRefSettingsParam(
    DataType.booleanValue,
    SettingsParamType.Boolean,
    headerFiltersVisibility,
    'headerFiltersVisibility',
    true,
  ),
  headerLocationFilterVisibility: typedRefSettingsParam(
    DataType.booleanValue,
    SettingsParamType.Boolean,
    headerFiltersVisibility,
    'headerFiltersVisibility',
    true,
  ),
  headerTimezoneSelectionVisibility: {
    type: SettingsParamType.Boolean,
    getDefaultValue: ({ isMobile, getSettingParamValue }) => {
      const layout =
        getSettingParamValue(calendarLayout) ||
        LayoutOptions.DAILY_TIME_SLOTS_MONTHLY_PICKER;

      return timeSlotLayouts.includes(layout) ? isMobile : false;
    },
  },
  dateAndTimeTimezoneSelectionVisibility: booleanSettingsParam(true),
  headerSubtitleSource: settingsParam(
    DataType.number,
    SubtitleSourceOptions.CUSTOM,
  ),
  calendarHeaderTitle: textSettingsParam(
    'app.settings.defaults.header-title-appointment',
  ),
  headerTitle: textSettingsParam('app.settings.defaults.header-title'),
  headerSubtitle: textSettingsParam('app.settings.defaults.header-subtitle'),
  noSessionsOffered: textSettingsParam(
    'app.settings.defaults.time-picker.no-slots-message',
  ),
  noUpcomingTimeSlots: textSettingsParam(
    'app.time-picker.notifications.no-upcoming-slots',
  ),
  goToNextAvailableDate: textSettingsParam(
    'app.settings.defaults.time-picker.go-to-next-available-day',
  ),
  fullyBookedDateNotification: textSettingsParam(
    'app.settings.defaults.time-picker.notifications.all-sessions-are-full',
  ),
  loadMoreTimeSlots: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t, getSettingParamValue, isMobile }) =>
      getSettingParamValue(calendarLayout, { isMobile }) ===
      LayoutOptions.WEEKLY_TIME_SLOTS
        ? t('app.settings.defaults.week-availability.show-all')
        : t('app.settings.defaults.time-picker.show-all'),
  },
  limitTimeSlotsDisplay: booleanSettingsParam(true),
  maxTimeSlotsDisplayedPerDay: {
    type: SettingsParamType.Number,
    getDefaultValue: ({ getSettingParamValue }) => {
      const layout =
        getSettingParamValue(calendarLayout) ||
        LayoutOptions.DAILY_TIME_SLOTS_MONTHLY_PICKER;
      return layout === LayoutOptions.WEEKLY_TIME_SLOTS ? 5 : 10;
    },
  },
  preferencesTitle: textSettingsParam(
    'app.settings.defaults.booking-details.preferences.title',
  ),
  serviceLabel: textSettingsParam('app.settings.defaults.service-label'),
  staffMemberLabel: textSettingsParam(
    'app.settings.defaults.staff-member-label',
  ),
  locationLabel: textSettingsParam('app.settings.defaults.location-label'),
  durationLabel: textSettingsParam('app.settings.defaults.duration-label'),
  bookingDetailsPricingPlanText: textSettingsParam(
    'app.settings.defaults.booking-details.pricing-plan.title',
  ),
  bookingDetailsClearText: textSettingsParam(
    'app.settings.defaults.booking-details.clear.title',
  ),
  nextButton: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t, getSettingParamValue, isMobile }) =>
      timeSlotLayouts.includes(
        getSettingParamValue(calendarLayout, { isMobile }),
      )
        ? t('app.settings.defaults.booking-details.book-now.text')
        : t('app.settings.defaults.button.book'),
  },
  toEarlyToBookIndication: textSettingsParam(
    'app.settings.defaults.policy.to-early-to-book',
  ),
  noSpotsLeft: textSettingsParam('app.settings.defaults.slots.no-spots-left'),
  toLateToBookIndication: textSettingsParam(
    'app.settings.defaults.policy.to-late-to-book',
  ),
  rescheduleButton: textSettingsParam(
    'app.rescheduled-booking.booking-details.cta',
  ),
  pendingApprovalButton: textSettingsParam(
    'app.settings.defaults.booking-details.pending-approval.text',
  ),
  joinWaitlistButton: textSettingsParam(
    'app.settings.defaults.booking-details.join-waitlist.text',
  ),
  videoConferenceBadgeVisibility: booleanSettingsParam(true),
  timeslotDetailsVisibility: booleanSettingsParam(true),
  videoConferenceBadge: textSettingsParam(
    'app.settings.defaults.video-conference-badge-text',
  ),
  slotsAvailability: settingsParam(DataType.number, SlotsAvailability.ALL),
  initializeCalendarDate: enumSettingsParam(
    InitializeCalendarDateOptions.TODAY,
  ),
  waitlistIndication: textSettingsParam('app.settings.defaults.waitlist'),
  buttonsFullWidth: booleanSettingsParamWithoutInheritedDesktop(true),
  slotLocationVisibility: booleanSettingsParam(false),
  slotRegistrationStatusVisibility: booleanSettingsParam(true),
  spotsLeftVisibility: booleanSettingsParam(true),
  slotPriceVisibility: booleanSettingsParam(false),
  slotStaffMemberVisibility: booleanSettingsParam(true),
  slotDurationVisibility: booleanSettingsParam(true),
  spotsLeftFormat: textSettingsParam('app.settings.defaults.slots.spots-left'),
  ctaVisibility: booleanSettingsParam(true),
  useColorPerService: {
    inheritDesktop: false,
    ...booleanSettingsParam(false),
  },
  servicesColorsType: enumSettingsParam(ServiceColor.BACKGROUND),
  sideBordersVisibility: booleanSettingsParam(true),
  timeSlotComponent: enumSettingsParam(TimeSlotComponent.BUTTONS),
};

export default createSettingsParams<ISettingsParams>(settingsParamsDefinitions);
